<!--
  @fileoverview Trending Topics Component - Popular Intent Suggestions (Vue 3)

  Displays a collapsible panel of trending topics that users can click
  to quickly navigate to popular conversation flows. The topics are
  fetched from the server and filtered based on the current language.

  @component TrendingTopics
  @displayName Trending Topics Panel

  Features:
  - Collapsible toggle panel
  - Language-aware topic display
  - Dynamic positioning based on header height
  - Click to send postback with trending intent
  - Animated dropdown toggle

  @example
  <TrendingTopics
    :is-open-list="isOpen"
    @handleTrendingList="toggleTrendingList"
    @sendPostback="handlePostback"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced mapGetters with useMainStore()
  - Replaced $store.dispatch with store action calls
  - Replaced $emit with defineEmits
-->
<template>
  <div class="trending" :style="{ '--trending-top': heightHeader + 'px' }" v-if="trendingTopics.length > 0">
    <div class="trending-button">
      <div class="trending-button-toggle cursor-pointer" @click="handleTrendingList">
        <p>Trending Topics</p>
        <div
          :class="[{ active: isOpenList }, { 'mr-[45px]': heightHeader === 0 }]"
          class="flex items-center"
        >
          <DropDownCustom :fill="settings.trendingTopicTitle" />
        </div>
      </div>
      <div class="trending-button-list" v-if="isOpenList">
        <div
          class="trending-button-list-item"
          v-for="(trending, i) in listTopicFilter"
          :key="i"
          @click="submitTrendingIntent(trending)"
        >
          <div>
            <CursorCustom :fill="settings.buttonOutlineColor" />
          </div>
          <p class="text-black">
            {{ getExampleTrendingTopic(trending) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/TrendingTopics/Index
 * @description Trending topics panel for quick navigation (Vue 3 Composition API)
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { getNodeIdByLanguage, getTextButton, getExampleByLanguage } from '@/helpers/trendingTopics'
import CursorCustom from '@/assets/images/CursorCustom.vue'
import DropDownCustom from '@/assets/images/DropDownCustom.vue'

/**
 * Component props
 */
const props = defineProps({
  /**
   * Whether the trending topics list is currently open
   */
  isOpenList: {
    type: Boolean,
    default: false,
  },
})

/**
 * Component emits
 * @event handleTrendingList - Emits to toggle the list open/close state
 * @event sendPostback - Emits postback data when a trending topic is clicked
 */
const emit = defineEmits(['handleTrendingList', 'sendPostback'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Reactive state
 */
const listTopicFilter = ref([])
const heightHeader = ref(0)

/**
 * Computed properties from store
 */
const trendingTopics = computed(() => store.trendingTopics)
const settings = computed(() => store.settings)
const currentLanguage = computed(() => store.currentLanguage)

/**
 * Get the display text for a trending topic based on current language
 *
 * @param {Object} trending - Trending topic object
 * @param {string} trending.intentId - The intent ID
 * @param {Array} trending.examples - Language-specific examples
 * @returns {string} The display text for the topic
 */
function getExampleTrendingTopic(trending) {
  const { intentId, examples } = trending
  if (!currentLanguage.value) return getTextButton(intentId)
  const example = getExampleByLanguage(currentLanguage.value, examples)
  if (example) return example
  return ''
}

/**
 * Handle clicking on a trending topic
 * Parses the intent and emits a postback event
 *
 * @param {Object} trending - The clicked trending topic
 * @emits sendPostback - Postback data with intent information
 */
function submitTrendingIntent(trending) {
  const { intentId, examples } = trending
  const nodeId = getNodeIdByLanguage(currentLanguage.value, examples)
  handleTrendingList(false)
  const trendingText = getExampleByLanguage(currentLanguage.value, examples)
  const button = JSON.parse(intentId)
  if (trendingText && nodeId) {
    button.data = nodeId
  }
  emit('sendPostback', { ...button, trendingText })
}

/**
 * Toggle the trending list visibility
 * Always closes the list (sets to false)
 *
 * @param {boolean} isOpen - Whether to open the list (unused, always closes)
 * @emits handleTrendingList - Emits false to close the list
 */
function handleTrendingList(isOpen = false) {
  emit('handleTrendingList', false)
}

/**
 * Filter topics that have displayable text in current language
 */
function getListTopicFilter() {
  listTopicFilter.value = trendingTopics.value.filter((data) =>
    getExampleTrendingTopic(data)
  )
}

/**
 * Initialize component on mount
 * - Calculate header height for positioning
 * - Fetch trending topics from server
 */
onMounted(async () => {
  const elementHeader = document.getElementById('keyreply-panel-header')
  if (elementHeader) {
    heightHeader.value = elementHeader.offsetHeight
  }

  // Fetch trending topics on mount
  await store.fetchTrendingTopics()
  getListTopicFilter()
})

/**
 * Watch for list open state changes
 * Re-fetches trending topics when list is opened
 */
watch(
  () => props.isOpenList,
  async (newValue) => {
    if (newValue) {
      await store.fetchTrendingTopics()
      getListTopicFilter()
    }
  }
)
</script>

<style lang="scss" scoped>
.trending {
  position: absolute;
  top: var(--trending-top, 60px);
  left: 0;
  width: 100%;
  background: white;
  z-index: -1;
  cursor: auto;
  &-button {
    &-toggle {
      color: var(--keyreply-trending-topic-title);
      background: var(--keyreply-trending-topic-banner);
      padding: 10px;
      display: flex;
      justify-content: space-between;
      transition: 0.3s all linear;
      align-items: center;
      > p {
        margin: 0;
        font-weight: 600;
      }
      > div {
        &.active {
          transform: rotateZ(180deg);
        }
      }
    }
    &-list {
      transition: 0.3s all linear;
      background: white;
      height: calc(100vh - 15px);
      margin-top: 15px;
      &-item {
        padding: 10px;
        display: flex;
        align-items: center;
        &:hover {
          background: #80808036;
          cursor: pointer;
        }
        > div {
          width: 20px;
          transform: rotate(90deg);
          margin-right: 15px;
        }
        > p {
          margin: 0;
        }
      }
    }
  }
}
</style>
